import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Leaf,
  Heart,
  Users,
  TrendingUp,
  BarChart3,
  ArrowRight,
  CheckCircle,
  Lightbulb,
  Globe,
  Zap,
  Building2,
  Plane,
  Database,
  Loader2,
  Rocket,
  Sparkles,
  Target,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const socTechPillars = [
  { icon: Leaf, label: 'Sustainability & Climate Action', color: 'bg-green-500/10 text-green-600' },
  { icon: Heart, label: 'Health Equity & Accessibility', color: 'bg-red-500/10 text-red-600' },
  { icon: Users, label: 'Community Empowerment', color: 'bg-blue-500/10 text-blue-600' },
  { icon: TrendingUp, label: 'Closing the Wealth Divide', color: 'bg-amber-500/10 text-amber-600' },
  { icon: BarChart3, label: 'Transparent Impact Measurement', color: 'bg-purple-500/10 text-purple-600' },
];

const useCases = [
  {
    id: 1,
    icon: Leaf,
    title: 'Carbon Transparency for SMEs',
    challenge: 'Smaller businesses struggle to measure and monetize their sustainability efforts.',
    solutions: [
      'Automated carbon footprint engines',
      'API-enabled carbon dashboards',
      'Digital eco-labeling systems',
      'Carbon-backed tokenization models',
      'MRV-ready sustainability reporting',
    ],
    impact: 'Democratizes access to climate finance and enables SMEs to participate in decarbonization markets.',
    color: 'border-green-500/30 bg-green-500/5',
    iconColor: 'bg-green-500/10 text-green-600',
  },
  {
    id: 2,
    icon: Zap,
    title: 'Community Energy Intelligence Platforms',
    challenge: 'Communities lack visibility into shared energy consumption and waste.',
    solutions: [
      'Real-time energy & water monitoring dashboards',
      'AI optimization for shared buildings',
      'Gamified community decarbonization apps',
      'ESG performance digital twins',
    ],
    impact: 'Empowers communities to collectively reduce emissions and operating costs.',
    color: 'border-amber-500/30 bg-amber-500/5',
    iconColor: 'bg-amber-500/10 text-amber-600',
  },
  {
    id: 3,
    icon: Heart,
    title: 'Health Equity Data Platforms',
    challenge: 'Underserved populations lack data visibility and access to preventive care.',
    solutions: [
      'Predictive analytics for early intervention',
      'Inclusive digital intake systems',
      'Telehealth access tools',
      'ESG-aligned healthcare impact dashboards',
    ],
    impact: 'Improves outcomes while reducing systemic healthcare cost burdens.',
    color: 'border-red-500/30 bg-red-500/5',
    iconColor: 'bg-red-500/10 text-red-600',
  },
  {
    id: 4,
    icon: TrendingUp,
    title: 'Inclusive Economic Participation Systems',
    challenge: 'Micro-entrepreneurs and informal businesses struggle to access funding and markets.',
    solutions: [
      'Digital identity & impact scoring',
      'Micro-impact financing platforms',
      'Transparent supply chain traceability',
      'Sustainability-linked procurement engines',
    ],
    impact: 'Reduces inequality and unlocks capital for underserved communities.',
    color: 'border-blue-500/30 bg-blue-500/5',
    iconColor: 'bg-blue-500/10 text-blue-600',
  },
  {
    id: 5,
    icon: Plane,
    title: 'Sustainable Tourism & Cultural Ecosystems',
    challenge: 'Tourism growth often erodes local culture and increases environmental strain.',
    solutions: [
      'Visitor carbon tracking tools',
      'AR/AI heritage storytelling platforms',
      'Sustainable marketplace platforms',
      'ESG dashboards for tourism hubs',
    ],
    impact: 'Balances economic vibrancy with cultural preservation and environmental stewardship.',
    color: 'border-purple-500/30 bg-purple-500/5',
    iconColor: 'bg-purple-500/10 text-purple-600',
  },
  {
    id: 6,
    icon: Database,
    title: 'Data-for-Good & ESG Open Analytics',
    challenge: 'Impact data is fragmented and often inaccessible.',
    solutions: [
      'Open data ecosystems',
      'Impact interoperability APIs',
      'Real-time ESG transparency portals',
      'Finance-grade MRV infrastructure',
    ],
    impact: 'Builds trust between investors, regulators, and communities.',
    color: 'border-teal-500/30 bg-teal-500/5',
    iconColor: 'bg-teal-500/10 text-teal-600',
  },
];

const hackathonParticipants = [
  'Startups & founders',
  'Developers & AI engineers',
  'Sustainability professionals',
  'Universities & researchers',
  'Corporates seeking impact pilots',
  'Government agencies & NGOs',
];

const hackathonBenefits = [
  'Access to sustainability data frameworks',
  'Impact measurement architecture',
  'Mentorship from domain experts',
  'Prototype-to-pilot pathway',
  'Exposure to investors & ecosystem partners',
];

const areaOfInterestOptions = [
  'Climate & Carbon',
  'Health Equity',
  'Community Platforms',
  'Inclusive Finance',
  'Tourism & Culture',
  'ESG & Impact Analytics',
];

const expertiseOptions = [
  'Developer',
  'Sustainability Professional',
  'Designer',
  'Policy / Regulatory',
  'Academic / Research',
  'Investor',
  'Corporate Innovation',
];

const participationOptions = [
  'Joining a Hackathon Team',
  'Sponsoring a Challenge',
  'Providing Data or APIs',
  'Becoming a Pilot Partner',
  'Exploring Investment',
  'General Collaboration',
];

export const SocTech = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    country: '',
    areasOfInterest: [],
    expertise: [],
    description: '',
    participationInterest: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleCheckboxChange = (field, value) => {
    setFormData((prev) => {
      const currentValues = prev[field];
      if (currentValues.includes(value)) {
        return { ...prev, [field]: currentValues.filter((v) => v !== value) };
      } else {
        return { ...prev, [field]: [...currentValues, value] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/soctech-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          organization: formData.organization,
          email: formData.email,
          country: formData.country,
          areas_of_interest: formData.areasOfInterest,
          expertise: formData.expertise,
          description: formData.description,
          participation_interest: formData.participationInterest,
        }),
      });

      if (!response.ok) throw new Error('Failed to submit');

      setIsSubmitted(true);
      toast.success('Interest submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="outline" className="mb-6 border-teal/30 text-teal bg-teal/10">
              Solutions / SocTech
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Social Impact Technology:{' '}
              <span className="text-teal">Engineered for Impact</span>
            </h1>
            <p className="text-xl text-white/70 mb-8 max-w-3xl mx-auto">
              Technology that moves society forward — not just balance sheets. We build digital solutions 
              designed to create measurable positive social and environmental outcomes.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#interest-form">
                <Button variant="teal" size="xl" className="group">
                  Join the Movement
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <Link to="/book-call">
                <Button variant="outline" size="xl" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                  Book a Discovery Call
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is SocTech */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4">What is SocTech?</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Social Impact Technology
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Social Impact Technology (SocTech) refers to innovative digital solutions intentionally 
                designed to create measurable positive social and environmental outcomes. Unlike conventional 
                enterprise technology — which often prioritizes productivity, automation, or cost reduction — 
                SocTech begins with <strong className="text-foreground">impact as the primary objective</strong>.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                Efficiency is important, but <strong className="text-foreground">equity, sustainability, 
                and community value come first</strong>.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {socTechPillars.map((pillar, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className={`w-10 h-10 rounded-lg ${pillar.color} flex items-center justify-center`}>
                      <pillar.icon className="h-5 w-5" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{pillar.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <Card className="border-teal/20 bg-gradient-to-br from-teal/5 to-accent/5">
                <CardContent className="pt-8 pb-8">
                  <div className="text-center">
                    <Sparkles className="h-16 w-16 text-teal mx-auto mb-6" />
                    <blockquote className="text-xl font-medium text-foreground italic mb-4">
                      "Impact without measurement is storytelling. Measurement without action is reporting. 
                      SocTech connects both — and makes it scalable."
                    </blockquote>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Our Philosophy</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Impact-Native by Design
            </h2>
            <p className="text-lg text-muted-foreground">
              At SusTek, we believe the next wave of innovation will be impact-native by design. 
              We combine cutting-edge technology with purpose-driven outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: Database, label: 'Sustainability Data Engineering' },
              { icon: Globe, label: 'ESG Digital Twin Modelling' },
              { icon: Lightbulb, label: 'AI-Driven Analytics' },
              { icon: Leaf, label: 'Carbon Accounting & MRV' },
              { icon: Users, label: 'Digital Engagement Platforms' },
              { icon: Heart, label: 'Inclusive Design Thinking' },
            ].map((item, index) => (
              <Card key={index} className="text-center hover-lift border-border/50">
                <CardContent className="pt-6 pb-6">
                  <div className="w-12 h-12 rounded-xl bg-teal/10 text-teal flex items-center justify-center mx-auto mb-3">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              To co-create solutions that are <strong className="text-foreground">financially viable, 
              socially equitable, and environmentally measurable</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Use Cases</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              SocTech Applications
            </h2>
            <p className="text-lg text-muted-foreground">
              Potential application areas where Social Impact Technology can drive transformative change.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase) => (
              <Card key={useCase.id} className={`hover-lift ${useCase.color} border`}>
                <CardHeader>
                  <div className={`w-12 h-12 rounded-xl ${useCase.iconColor} flex items-center justify-center mb-3`}>
                    <useCase.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-lg">{useCase.title}</CardTitle>
                  <CardDescription className="text-sm">
                    <strong>Challenge:</strong> {useCase.challenge}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-foreground mb-2">SocTech Solutions:</p>
                    <ul className="space-y-1">
                      {useCase.solutions.map((solution, idx) => (
                        <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2">
                          <CheckCircle className="h-3 w-3 text-teal flex-shrink-0 mt-0.5" />
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="pt-3 border-t border-border/50">
                    <p className="text-xs">
                      <strong className="text-foreground">Impact:</strong>{' '}
                      <span className="text-muted-foreground">{useCase.impact}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* From Concept to Impact */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Our Approach</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              From Concept to Scalable Impact
            </h2>
            <p className="text-lg text-muted-foreground">
              SocTech is not built in isolation. It requires collaboration across sectors.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: Users, label: 'Cross-sector Collaboration' },
              { icon: Building2, label: 'Policy Alignment' },
              { icon: Database, label: 'Technical Architecture' },
              { icon: Target, label: 'Measurable Impact Frameworks' },
              { icon: TrendingUp, label: 'Financial Sustainability Models' },
            ].map((item, index) => (
              <Card key={index} className="text-center border-border/50 bg-card">
                <CardContent className="pt-6 pb-6">
                  <div className="w-14 h-14 rounded-full bg-teal/10 text-teal flex items-center justify-center mx-auto mb-4">
                    <item.icon className="h-7 w-7" />
                  </div>
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-foreground font-medium">
              That's why we invite <span className="text-teal">innovators, enterprises, developers, academics, 
              NGOs, and policymakers</span> to co-create with us.
            </p>
          </div>
        </div>
      </section>

      {/* Hackathon Section */}
      <section className="py-20 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4 border-teal/30 text-teal bg-teal/10">
                Co-Innovation
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                SocTech Co-Innovation Hackathon Series
              </h2>
              <p className="text-lg text-white/70 mb-6">
                We're launching a hackathon series to prototype scalable solutions across climate, 
                community, health, and inclusive growth.
              </p>
              <p className="text-white/80 font-medium mb-8">
                This is not just a hackathon. It's a launchpad for measurable societal impact.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Users className="h-5 w-5 text-teal" />
                    Who Should Join?
                  </h3>
                  <ul className="space-y-2">
                    {hackathonParticipants.map((participant, idx) => (
                      <li key={idx} className="text-white/70 text-sm flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-teal" />
                        {participant}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Rocket className="h-5 w-5 text-teal" />
                    What You'll Gain
                  </h3>
                  <ul className="space-y-2">
                    {hackathonBenefits.map((benefit, idx) => (
                      <li key={idx} className="text-white/70 text-sm flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-teal" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-teal/20 rounded-2xl blur-xl" />
              <Card className="relative border-teal/20 bg-navy/80 backdrop-blur">
                <CardContent className="pt-8 pb-8 text-center">
                  <Rocket className="h-20 w-20 text-teal mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-4">Build the Future of Impact</h3>
                  <p className="text-white/70 mb-6">
                    Join innovators, developers, and changemakers to create technology that improves lives.
                  </p>
                  <a href="#interest-form">
                    <Button variant="teal" size="lg" className="group">
                      Register Interest
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Interest Form */}
      <section id="interest-form" className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Join the Movement</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              SocTech Co-Innovation Interest Form
            </h2>
            <p className="text-lg text-muted-foreground">
              Let's co-create technology that improves lives. Complete the form below to participate.
            </p>
          </div>

          <Card className="border-border/50 bg-card">
            <CardContent className="pt-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-teal" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Interest Submitted!</h3>
                  <p className="text-muted-foreground mb-6">
                    Thank you for your interest in SocTech. We'll be in touch soon with more details.
                  </p>
                  <Button variant="outline" onClick={() => setIsSubmitted(false)}>
                    Submit Another
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="organization">Organization *</Label>
                      <Input
                        id="organization"
                        placeholder="Your organization"
                        value={formData.organization}
                        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        placeholder="Your country"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <Label>Area of Interest *</Label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {areaOfInterestOptions.map((option) => (
                        <div key={option} className="flex items-center space-x-2">
                          <Checkbox
                            id={`area-${option}`}
                            checked={formData.areasOfInterest.includes(option)}
                            onCheckedChange={() => handleCheckboxChange('areasOfInterest', option)}
                            disabled={isSubmitting}
                          />
                          <label htmlFor={`area-${option}`} className="text-sm text-muted-foreground cursor-pointer">
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>Your Expertise *</Label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {expertiseOptions.map((option) => (
                        <div key={option} className="flex items-center space-x-2">
                          <Checkbox
                            id={`expertise-${option}`}
                            checked={formData.expertise.includes(option)}
                            onCheckedChange={() => handleCheckboxChange('expertise', option)}
                            disabled={isSubmitting}
                          />
                          <label htmlFor={`expertise-${option}`} className="text-sm text-muted-foreground cursor-pointer">
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Brief Description of Your Idea or Interest</Label>
                    <Textarea
                      id="description"
                      placeholder="Tell us about your idea or what you'd like to contribute (300 words max)"
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label>Are you interested in:</Label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {participationOptions.map((option) => (
                        <div key={option} className="flex items-center space-x-2">
                          <Checkbox
                            id={`participation-${option}`}
                            checked={formData.participationInterest.includes(option)}
                            onCheckedChange={() => handleCheckboxChange('participationInterest', option)}
                            disabled={isSubmitting}
                          />
                          <label htmlFor={`participation-${option}`} className="text-sm text-muted-foreground cursor-pointer">
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button type="submit" variant="teal" size="lg" className="w-full group" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Interest
                        <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
            The Future is Impact-Native
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Technology will define the next decade. The question is not whether we innovate. 
            The question is <strong className="text-foreground">who benefits from that innovation</strong>. 
            Let's build systems that scale prosperity, resilience, and sustainability — together.
          </p>
          <Link to="/book-call">
            <Button variant="teal" size="xl" className="group">
              Let's Build Together
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SocTech;
